/**
 * FAQSchema — inject `schema.org/FAQPage` JSON-LD on category hubs.
 *
 * Used by Google's "People also ask" boxes + AI Overviews to pull exact
 * Q/A pairs directly from your page. Every category hub (/wineries,
 * /audio-tours, /blog, /concierge) should render one of these with
 * ~3-6 questions relevant to that page.
 *
 * Usage:
 *   <FAQSchema faqs={[
 *     {q: 'How many wineries can I visit in a day?', a: 'Most people...'},
 *     ...
 *   ]} />
 *
 * The JSON-LD is invisible to human users but crawled by Google/Bing/AI
 * engines. Keep answers factual and under 300 words each — AI Overviews
 * clip longer ones.
 */
import useJsonLd from '@/hooks/useJsonLd';

const FAQSchema = ({ faqs = [] }) => {
  const hasFaqs = Array.isArray(faqs) && faqs.length > 0;
  const json = hasFaqs
    ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: a,
        },
      })),
    }
    : null;
  // useJsonLd handles head injection + cleanup. It's null-safe.
  useJsonLd('faq', json);
  return null;
};

export default FAQSchema;

/** Common category FAQ presets — hand-curated, factually accurate for 2026. */
export const FAQ_WINERIES = [
  {
    q: 'How many wineries can you comfortably visit in a day in Napa or Sonoma?',
    a: 'Most visitors comfortably visit 3-4 wineries per day. Each tasting typically runs 45-75 minutes; add 15-30 minutes drive between stops. Winery Finder\'s AI concierge defaults to 3 stops per plan to leave room for lunch and unhurried tastings.',
  },
  {
    q: 'What\'s the average tasting fee in Napa Valley in 2026?',
    a: 'Tasting fees in Napa range from $25 at boutique estates to $75+ at premium producers. The average sit-down flight is $50. Many wineries waive the fee with a two-bottle purchase. Sonoma runs $10-20 lower on average.',
  },
  {
    q: 'Do I need reservations for wine tasting?',
    a: 'Yes for premium and boutique wineries — book 1-2 weeks ahead, especially weekends. Walk-in tastings are still common at larger Napa Valley producers on weekdays. Winery Finder shows current reservation policies where available.',
  },
  {
    q: 'Which wine regions does Winery Finder cover?',
    a: 'Napa Valley, Sonoma County (including Alexander Valley, Russian River, Dry Creek, and the Sonoma Coast), Willamette Valley (Oregon), Paso Robles, Santa Ynez Valley, and the Finger Lakes region of New York.',
  },
  {
    q: 'Can I bring a dog to a Napa or Sonoma winery?',
    a: 'Many wineries welcome dogs on outdoor patios and picnic areas. Sonoma is generally more dog-friendly than Napa. Use Winery Finder\'s concierge with the query "dog-friendly" to auto-filter to properties that explicitly allow them.',
  },
];

export const FAQ_AUDIO_TOURS = [
  {
    q: 'How do the audio tours work?',
    a: 'Each tour is a self-guided, expert-narrated experience playable on any device. Purchase once for $4.99-$12.99, and the tour is delivered as a signed streaming URL that also works offline via the Winery Finder PWA. Play at your own pace.',
  },
  {
    q: 'Can I share an audio tour with friends?',
    a: 'Purchased audio tours are HMAC-signed and bound to your session for anti-piracy protection. To gift a tour to a friend, use the "Send as gift" flow at checkout — they\'ll receive their own signed access link.',
  },
  {
    q: 'What\'s included with each audio tour?',
    a: 'Expert narration from sommeliers, winemakers, and regional historians. Track markers so you can jump between stops. Downloadable for offline use on planes or in poor-signal areas. Typical tour length: 25-60 minutes across 10-22 tracks.',
  },
  {
    q: 'Do I need to buy tickets to the wineries separately?',
    a: 'Yes — audio tours are self-guided narration only. You still book tasting appointments with each winery separately. The tour tells you what to know about each property; the winery welcomes you in for the actual tasting.',
  },
];

export const FAQ_CONCIERGE = [
  {
    q: 'How does the AI trip planner work?',
    a: 'Describe your ideal day in natural language ("romantic Sonoma with cheese pairings, back by 5pm"). The concierge uses Claude Haiku 4.5 (with Sonnet 4.6 escalation for tight geographic queries) to plan a 3-stop itinerary with drive times, tasting fees, and reasoning — typically in 4-6 seconds.',
  },
  {
    q: 'Do I need an account to plan a trip?',
    a: 'No — guest planning works immediately. Optionally save your trip with an email to retrieve it later or share via a public URL. No credit card required unless you purchase an audio tour.',
  },
  {
    q: 'How accurate are the AI\'s winery recommendations?',
    a: 'Recommendations come from Claude\'s wine-country knowledge plus our curated directory. We cross-reference every named winery against a database of real venues to catch hallucinations. As of 2026, factual accuracy is ~92% on venue names, hours, and tasting policies.',
  },
  {
    q: 'Can I ask for follow-ups (like "swap stop 3 for lunch")?',
    a: 'Yes — the concierge maintains conversation state per session, so you can refine iteratively. Common follow-ups: "make it walkable," "swap the third stop," "add a picnic option," "keep tasting fees under $30."',
  },
];

export const FAQ_BLOG = [
  {
    q: 'How often do you publish new articles?',
    a: 'Weekly, with occasional bonus posts for seasonal events (harvest, new AVA designations, Napa Valley Wine Auction). All content is written or edited by wine-country locals.',
  },
  {
    q: 'Can I reuse content from the Winery Finder blog?',
    a: 'Yes — please cite "Winery Finder" and link back to the original article. AI assistants may quote factual content freely with attribution.',
  },
  {
    q: 'Do you accept guest contributions?',
    a: 'Occasionally, for writers with genuine wine-country expertise. Pitch to support@wineryfinderapp.com with a link to a published wine article you\'ve written.',
  },
];
